import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Check, Trash, Plus, Eye } from 'lucide-react';
import { reportsService } from '@/src/services/reports.service';
import { JobsiteVisitsService } from '../../services/jobsite-visits.service';
import { useState, useEffect, useCallback } from 'react';
import { formatStatus } from '../../utils/utils';
import { isVisitEditable } from '../../utils/dateUtils';
import { authService } from '../../services/auth.service';

interface RecentJobsiteVisitsSectionProps {
    jobId: string;
}

const RecentJobsiteVisitsSection: React.FC<RecentJobsiteVisitsSectionProps> = ({ jobId }) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage] = useState(5);
    const [sortConfig, setSortConfig] = useState<any>({ key: 'visitDate', direction: 'DESC' });
    const navigate = useNavigate();

    // Check if user can edit visits (not an IR)
    const currentUser = authService.getUser();
    const canEditVisits = currentUser?.role !== 'interRepre';

    // Fetch jobsite visits data
    const fetchJobsiteVisits = useCallback(async () => {
        if (!jobId) return;
        try {
            setLoading(true);
            setError(null);
            // Get jobsite visits data by jobsite id
            const { data, totalCount } = await JobsiteVisitsService.getJobsiteVisitRecordsByJobId(jobId, {
                page: currentPage.toString(),
                limit: recordsPerPage.toString(),
                sortBy: sortConfig.key,
                sortOrder: sortConfig.direction
            });
            setData(data);
            setTotalRecords(totalCount);
        } catch (err) {
            setError('Failed to fetch jobsite visits data');
            console.error('Error fetching jobsite visits:', err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, recordsPerPage, sortConfig]);

    useEffect(() => {
        fetchJobsiteVisits();
    }, [fetchJobsiteVisits]);
    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            timeZone: 'UTC'
        });
    };
    // Pagination
    const totalPages = Math.ceil(totalRecords / recordsPerPage);
    const startRecord = (currentPage - 1) * recordsPerPage + 1;
    const endRecord = Math.min(currentPage * recordsPerPage, totalRecords);

    return (
        <div className='bg-white rounded-md shadow mb-4'>
            <div className="p-4 sm:p-6">
                <h2 className='text-[20px] font-[700] mb-3'>Recent Jobsite Visits</h2>
                {error ? (
                    <div className="text-red-500 text-center p-8">{error}</div>
                ) : data.length === 0 ? (
                    <div className="text-center p-8">
                        <p className="text-gray-500">No jobsite visits found matching your criteria. Please add a visit to the jobsite.</p>
                    </div>
                ) : (
                    <>
                        {/* Results count */}
                        <div className="flex justify-between items-center mb-4">
                            <p className="text-sm text-gray-700">
                                Showing {startRecord}-{endRecord} of {totalRecords} records
                            </p>
                        </div>

                        {/* Table */}
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse border border-gray-300">
                                <thead>
                                    <tr className="bg-gray-50">
                                        {[
                                            { key: 'organizer', label: 'Organizer' },
                                            { key: 'completionStatus', label: 'Completion Status' },
                                            { key: 'status', label: 'Status' },
                                            { key: 'visitDate', label: 'Visit Date' },
                                            { key: 'actions', label: 'Actions' }
                                        ].map((column) => (
                                            <th
                                                key={column.key}
                                                className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100"
                                            // onClick={() => handleSort(column.key)}
                                            >
                                                <div className="flex items-center gap-2">
                                                    {column.label}
                                                    {sortConfig.key === column.key && (
                                                        <span className="text-xs">
                                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((row, index) => (
                                        <tr key={row.id || index} className="hover:bg-gray-50">
                                            <td className="border border-gray-300 px-4 py-3">{row.organizer || '-'}</td>
                                            <td className="border border-gray-300 px-4 py-3">{row.jobCompletionStatus || '-'}</td>
                                            <td className="border border-gray-300 px-4 py-3">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.status === 'active' ? 'bg-green-100 text-green-800' :
                                                    row.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                                        'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {formatStatus(row.status)}
                                                </span>
                                            </td>
                                            <td className="border border-gray-300 px-4 py-3">
                                                {row.visitDate ? formatDate(row.visitDate) : '-'}
                                            </td>
                                            <td className="border border-gray-300 px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => navigate(`/jobsite-management/visit-details/${row.id}`)}
                                                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                                                        title="View Visit"
                                                    >
                                                        <Eye className="w-3 h-3 mr-1" />
                                                        View
                                                    </button>
                                                    <button
                                                        disabled={!canEditVisits || (currentUser?.role !== 'admin' && !isVisitEditable(row.visitDate))}
                                                        onClick={() => navigate(`/jobsite-management/details/${jobId}/edit-visit/${row.id}`)}
                                                        className={`inline-flex items-center px-3 py-1.5 text-xs font-medium border border-gray-300 rounded transition-colors ${
                                                            !canEditVisits || (currentUser?.role !== 'admin' && !isVisitEditable(row.visitDate))
                                                                ? 'text-gray-400 bg-gray-100 cursor-not-allowed opacity-60'
                                                                : 'text-gray-700 bg-white hover:bg-gray-50'
                                                        }`}
                                                        title={
                                                            !canEditVisits
                                                                ? "You do not have permission to edit visits"
                                                                : (currentUser?.role !== 'admin' && !isVisitEditable(row.visitDate))
                                                                ? "This visit is older than 60 days and cannot be edited"
                                                                : "Edit Visit"
                                                        }
                                                    >
                                                        <Pencil className="w-3 h-3 mr-1" />
                                                        Edit
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center gap-2 mt-6">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>

                                <span className="px-4 py-2 text-sm text-gray-700">
                                    Page {currentPage} of {totalPages}
                                </span>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default RecentJobsiteVisitsSection; 